import dayjs from "@/lib/dayjsConfig";

// ---- Helper pure functions ----
function savedEventsReducer(state, { type, payload }) {
  switch (type) {
    case "push":
      return [...state, payload];
    case "update":
      return state.map((evt) => (evt.id === payload.id ? payload : evt));
    case "delete":
      return state.filter((evt) => evt.id !== payload.id);
    default:
      return state;
  }
}

function deriveLabels(savedEvents, existingLabels = []) {
  const unique = [...new Set(savedEvents.map((e) => e.label))];
  return unique.map((label) => {
    const found = existingLabels.find((l) => l.label === label);
    return { label, checked: found ? found.checked : true };
  });
}

function filterEvents(savedEvents, labels) {
  const active = labels.filter((l) => l.checked).map((l) => l.label);
  return savedEvents.filter((evt) => active.includes(evt.label));
}

// Slice factory (pure)
export const createCalendarSlice = (set, get) => {
  return {
    // UI state
    monthIndex: dayjs().month(),
    setMonthIndex: (idx) => set({ monthIndex: idx }),
    // View mode: 'month' | 'week' | 'day'
    viewMode: "month",
    setViewMode: (mode) => set({ viewMode: mode }),
    smallCalendarMonth: null,
    setSmallCalendarMonth: (idx) => set({ smallCalendarMonth: idx }),
    daySelected: dayjs(),
    setDaySelected: (day) => set({ daySelected: day }),
    showEventModal: false,
    setShowEventModal: (val) => set({ showEventModal: val }),
    selectedEvent: null,
    setSelectedEvent: (evt) => set({ selectedEvent: evt }),

    // Data state (driven by TanStack)
    savedEvents: [],
    labels: [],
    filteredEvents: [],

    // Multiple calendars state
    calendars: [], // [{ id, name, color?, lastModified? }]
    selectedCalendarId: null,
    setCalendars: (calendars) =>
      set((state) => {
        const next = { calendars };
        // Auto-select the first calendar if none is selected
        if (!state.selectedCalendarId && calendars && calendars.length > 0) {
          next.selectedCalendarId = calendars[0].id;
        }
        return next;
      }),
    selectCalendar: (id) => set({ selectedCalendarId: id }),
    addCalendarLocal: (cal) =>
      set((state) => ({ calendars: [...state.calendars, cal] })),

    // Create calendar UI state
    isCreatingCalendar: false,
    setIsCreatingCalendar: (val) => set({ isCreatingCalendar: val }),
    calendarForm: {
      name: "",
      title: "",
      timezone: "UTC",
    },
    setCalendarFormField: (field, value) =>
      set((state) => ({
        calendarForm: { ...state.calendarForm, [field]: value },
      })),
    resetCalendarForm: () =>
      set({
        calendarForm: { name: "", title: "", timezone: "UTC" },
      }),

    // Set all events from server
    setEvents: (events) =>
      set((state) => {
        const labels = deriveLabels(events, state.labels);
        return {
          savedEvents: events,
          labels,
          filteredEvents: filterEvents(events, labels),
        };
      }),

    setLabels: (newLabels) =>
      set((state) => ({
        labels: newLabels,
        filteredEvents: filterEvents(state.savedEvents, newLabels),
      })),

    updateLabel: (label) =>
      set((state) => {
        const nextLabels = state.labels.map((l) =>
          l.label === label.label ? label : l
        );
        return {
          labels: nextLabels,
          filteredEvents: filterEvents(state.savedEvents, nextLabels),
        };
      }),

    // Keep for local-only updates if needed by UI (not used by server mutations)
    dispatchCalEvent: ({ type, payload }) =>
      set((state) => {
        const updated = savedEventsReducer(state.savedEvents, {
          type,
          payload,
        });
        const nextLabels = deriveLabels(updated, state.labels);
        return {
          savedEvents: updated,
          labels: nextLabels,
          filteredEvents: filterEvents(updated, nextLabels),
        };
      }),

    forceRecomputeFiltered: () => {
      const { savedEvents, labels } = get();
      set({ filteredEvents: filterEvents(savedEvents, labels) });
    },
  };
};
